import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import pick from 'lodash.pick';
import {fetchSenders, fetchAllSenders} from '../../Delegation/sendersSlice';
import {getErrorMessage} from '../../../utils/requests/error';
import type {ErrorMessage} from '@utils/requests/error.types';
import {getActiveAccountId} from '@utils/account';
import {getResponseBody} from '@utils/apiResponse';
import {userApi} from '../../../../api/user/api';
import {accountUserApi} from '../../../../api/accountUser/api';
import {permissionApi} from '../../../../api/permission/api';
import type {RoleResource} from '../../../../api/permission/api.d';
import type {User, CreateUserRequest} from '../../../../api/user/api.d';

export interface UsersState {
    roles: RoleResource[];
    userRoles: RoleResource[];
    usersRoles: Record<string, RoleResource[]>;
    isRolesFetching: boolean;
    isUsersRolesFetching: boolean;
    rolesFetchingError: ErrorMessage | null;
    isUserSaving: boolean;
    userSavingError: ErrorMessage | null;
    isUserRolesSaving: boolean;
    userRolesSavingError: ErrorMessage | null;
    isFetchingImageSignature: boolean;
}

const initialState: UsersState = {
    roles: [],
    userRoles: [],
    usersRoles: {},
    isRolesFetching: false,
    isUsersRolesFetching: false,
    rolesFetchingError: null,
    isUserSaving: false,
    userSavingError: null,
    isUserRolesSaving: false,
    userRolesSavingError: null,
    isFetchingImageSignature: false
};

export const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setRoles: (state, action: PayloadAction<RoleResource[]>) => {
            state.roles = action.payload;
        },
        setUserRoles: (state, action: PayloadAction<RoleResource[]>) => {
            state.userRoles = action.payload;
        },
        setUsersRoles: (state, action: PayloadAction<Record<string, RoleResource[]>>) => {
            state.usersRoles = action.payload;
        },
        setIsRolesFetching: (state, action: PayloadAction<boolean>) => {
            state.isRolesFetching = action.payload;
        },
        setIsUsersRolesFetching: (state, action: PayloadAction<boolean>) => {
            state.isUsersRolesFetching = action.payload;
        },
        setRolesFetchingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.rolesFetchingError = action.payload;
        },
        setIsUserSaving: (state, action: PayloadAction<boolean>) => {
            state.isUserSaving = action.payload;
        },
        setUserSavingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.userSavingError = action.payload;
        },
        setIsUserRolesSaving: (state, action: PayloadAction<boolean>) => {
            state.isUserRolesSaving = action.payload;
        },
        setUserRolesSavingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.userRolesSavingError = action.payload;
        }
    }
});

export const {
    setRoles,
    setUserRoles,
    setUsersRoles,
    setIsRolesFetching,
    setIsUsersRolesFetching,
    setRolesFetchingError,
    setIsUserSaving,
    setUserSavingError,
    setIsUserRolesSaving,
    setUserRolesSavingError
} = usersSlice.actions;

async function getAccountUserRoles(accountId: string, userId: string, accountUserId?: string): Promise<RoleResource[]> {
    const resolvedAccountUserId = accountUserId
        || getResponseBody<{id: string}>(await accountUserApi.getAccountUserByAccountAndUser({accountId, userId})).id;
    const response = await permissionApi.getAccountUserRoles(resolvedAccountUserId);
    const body = getResponseBody<RoleResource[] | {roles?: RoleResource[]}>(response);
    return Array.isArray(body) ? body : (body?.roles ?? []);
}

export const fetchRoles = () => async (dispatch: any, getState: any) => {
    const accountId = getActiveAccountId(getState().user);

    dispatch(setIsRolesFetching(true));
    dispatch(setRolesFetchingError(null));

    try {
        const roles = await permissionApi.getAllRoles(accountId);
        dispatch(setRoles(roles));
        dispatch(setIsRolesFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsRolesFetching(false));
        dispatch(setRolesFetchingError(getErrorMessage(err)));
        return false;
    }
};

export const fetchUserRoles = ({userId, accountId}: {userId: string; accountId: string}) => async (dispatch: any) => {
    dispatch(setIsRolesFetching(true));
    dispatch(setRolesFetchingError(null));

    try {
        const roles = await getAccountUserRoles(accountId, userId);
        dispatch(setUserRoles(roles));
        dispatch(setIsRolesFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsRolesFetching(false));
        dispatch(setRolesFetchingError(getErrorMessage(err)));
        return false;
    }
};

export const fetchUsersRoles = (users: Array<{userId: string; accountUserId?: string}> = [], accountId: string) => async (dispatch: any) => {
    dispatch(setIsUsersRolesFetching(true));

    try {
        const usersRoles: Record<string, RoleResource[]> = {};

        await Promise.all(users.map(async ({userId, accountUserId}) => {
            usersRoles[userId] = await getAccountUserRoles(accountId, userId, accountUserId);
        }));

        dispatch(setUsersRoles(usersRoles));
        dispatch(setIsUsersRolesFetching(false));
        return true;
    } catch {
        dispatch(setIsUsersRolesFetching(false));
        return false;
    }
};

async function getExistingUser(email: string): Promise<User | null> {
    try {
        const response = await userApi.getUserByEmail({params: {email}});
        return getResponseBody<User | null>(response) || null;
    } catch {
        return null;
    }
}

async function getIsUserInAccount(accountId: string, userId: string): Promise<boolean> {
    try {
        const response = await accountUserApi.getAccountUserByAccountAndUser({accountId, userId});
        return Boolean(getResponseBody<{id?: string}>(response)?.id);
    } catch {
        return false;
    }
}

const SENDER_ALREADY_MEMBER_MESSAGE_KEY = 'esl.error.validation.senderAlreadyMember';

async function createUser(user: CreateUserRequest, accountId: string): Promise<User> {
    const existingUser = await getExistingUser(user.email);

    if (existingUser) {
        const isUserInAccount = await getIsUserInAccount(accountId, existingUser.id);

        if (isUserInAccount) {
            const error = new Error(SENDER_ALREADY_MEMBER_MESSAGE_KEY) as Error & {data: {messageKey: string}};
            error.data = {messageKey: SENDER_ALREADY_MEMBER_MESSAGE_KEY};
            throw error;
        }

        await accountUserApi.attachUserToAccount({
            accountId,
            userId: existingUser.id
        });

        return existingUser;
    }

    const response = await userApi.createUser({
        data: {
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            organisation: user.organisation || undefined,
            title: user.title || undefined
        }
    });
    const createdUser = getResponseBody<User>(response);

    await accountUserApi.attachUserToAccount({
        accountId,
        userId: createdUser.id
    });

    return createdUser;
}

async function syncUserRoles(accountId: string, userId: string, roles: RoleResource[]) {
    const accountUserResponse = await accountUserApi.getAccountUserByAccountAndUser({
        accountId,
        userId
    });
    const accountUserId = getResponseBody<{id: string}>(accountUserResponse).id;

    return permissionApi.syncAccountUserRoles(accountUserId, {
        roles: roles.map((role) => role.id)
    });
}

export const saveUser = (user: Partial<CreateUserRequest> & {id?: string} = {}, roles: RoleResource[] = []) => async (dispatch: any, getState: any) => {
    const accountId = getActiveAccountId(getState().user);
    let userId = user.id;

    dispatch(setIsUserSaving(true));
    dispatch(setUserSavingError(null));

    try {
        if (!userId) {
            const userPayLoad = pick(user, ['first_name', 'last_name', 'email', 'organisation', 'title']) as CreateUserRequest;
            const createdUser = await createUser(userPayLoad, accountId);
            userId = createdUser.id;
        }

        if (userId) {
            await syncUserRoles(accountId, userId, roles);
        }

        await dispatch(fetchSenders());
        dispatch(fetchAllSenders({countOnly: true}));
        await dispatch(fetchUserRoles({userId: userId!, accountId}));
        dispatch(setIsUserSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsUserSaving(false));
        dispatch(setUserSavingError(getErrorMessage(err)));
        return false;
    }
};

export const selectRoles = (state: any) => state.users.roles;
export const selectUsersRoles = (state: any) => state.users.usersRoles;
export const selectUserRoles = (state: any) => state.users.userRoles;
export const selectIsRolesFetching = (state: any) => state.users.isRolesFetching;
export const selectIsUsersRolesFetching = (state: any) => state.users.isUsersRolesFetching;
export const selectRolesFetchingError = (state: any) => state.users.rolesFetchingError;
export const selectIsUserSaving = (state: any) => state.users.isUserSaving;
export const selectUserSavingError = (state: any) => state.users.userSavingError;

export default usersSlice.reducer;
